/**
 * 처방 판정 테스트 패널 — 약품 카드 파생 순수 함수(설계 결정 11).
 *
 * "판정 해석은 순수 함수, 화면은 조립"(§8) — 카드 배지 등급 선택, 채택/밀린 판정 분리,
 * 검수 대기 판정, 강등 약품 수, 해석 단위 줄 등을 화면과 분리해 단위 테스트로 고정한다.
 */
import type {
  DoseReviewData,
  DoseReviewItem,
  DoseFinding,
  Severity,
  DoseStatusStr,
  DoseReviewRequest,
} from "api/doseReviewService";
import { severityRank } from "./verdict";

/** 카드 배지·정렬에 쓰는 등급 — 강등 판정은 원래 등급(from)으로 본다(설계 결정 10). */
export const effectiveSeverity = (f: DoseFinding): Severity =>
  f.pendingDowngrade ? f.pendingDowngrade.from : f.severity;

/** 채택 판정(applied === true). */
export const appliedFindings = (item: DoseReviewItem): DoseFinding[] =>
  item.findings.filter((f) => f.applied);

/** 밀린 판정(applied === false). */
export const supersededFindings = (item: DoseReviewItem): DoseFinding[] =>
  item.findings.filter((f) => !f.applied);

/** 미판정 카드 여부(unevaluatedReason ≠ null) — 배지·표식을 그리지 않는다(AC-36). */
export const isUnevaluated = (item: DoseReviewItem): boolean => item.unevaluatedReason != null;

/** 채택 판정을 등급 순위 내림차순으로 정렬(AC-51). 강등은 from 기준. */
export const sortedApplied = (item: DoseReviewItem): DoseFinding[] =>
  [...appliedFindings(item)].sort(
    (a, b) => severityRank(effectiveSeverity(b)) - severityRank(effectiveSeverity(a))
  );

/**
 * 카드 대표 배지 — 채택 판정 중 가장 높은 등급(강등은 from). 미판정·채택 0건이면 없음.
 * downgraded면 흐린 테두리형(Outlined)으로 그린다(설계 결정 10).
 *
 * status는 대표 판정(win)의 것을 함께 낸다 — 라벨·톤이 status 우선이라(미달은
 * severity가 warning이든 info든 "1회 부족(참고)") 화면이 win의 status를 알아야 한다
 * (@spec:2026-09-18-처방검토-미달경고-참고등급 D-5).
 */
export const cardBadge = (
  item: DoseReviewItem
): { severity: Severity; status: DoseStatusStr; downgraded: boolean } | undefined => {
  if (isUnevaluated(item)) return undefined;
  const applied = appliedFindings(item);
  if (applied.length === 0) return undefined;
  let win = applied[0];
  for (const f of applied) {
    if (severityRank(effectiveSeverity(f)) > severityRank(effectiveSeverity(win))) win = f;
  }
  return {
    severity: effectiveSeverity(win),
    status: win.status,
    downgraded: win.pendingDowngrade != null,
  };
};

/** "검수 대기" 표식 — 채택 판정에 pendingReview:true가 하나라도 있을 때(AC-40·40b·40c). */
export const cardIsPending = (item: DoseReviewItem): boolean =>
  !isUnevaluated(item) && appliedFindings(item).some((f) => f.pendingReview);

/** 요약 줄 강등 표기 M — 채택 판정에 pendingDowngrade가 있는 약품 수(설계 결정 10). */
export const downgradedItemCount = (data: DoseReviewData): number =>
  data.items.filter((item) => appliedFindings(item).some((f) => f.pendingDowngrade != null)).length;

/** 카드 제목 이름 — 요청의 medicationName(응답 resolved.productName 아님, AC-69). */
export const cardTitleName = (request: DoseReviewRequest, index: number): string =>
  request.prescriptions[index]?.medicationName ?? "";

/** 해석 단위 줄(AC-47) — prescribeUnit이 null이면 그리지 않는다(null 반환). */
export const interpretUnitLine = (
  dose: number | undefined,
  prescribeUnit: string | null | undefined
): string | null => (prescribeUnit ? `입력 ${dose ?? ""} → ${prescribeUnit}로 해석` : null);

/** 밀린 룰 한 줄(AC-46) — `#{ruleId} · {라벨} · #{supersededBy}에 밀림`[· 검수 대기]. */
export const supersededLine = (
  f: DoseFinding,
  label: string
): string => {
  const base = `#${f.ruleId} · ${label} · #${f.supersededBy}에 밀림`;
  return f.pendingReview ? `${base} · 검수 대기` : base;
};
